import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export default function Privacy() {
    return (
        <div className="min-h-screen grain" data-testid="privacy-page">
            <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 prose-vu">
                <Link to="/" className="btn-ghost text-sm inline-flex items-center gap-1 mb-6">
                    <ChevronLeft size={14} /> Back home
                </Link>
                <div className="font-script text-xl text-[var(--brand-orange)]">virtually unstoppable</div>
                <h1 className="font-serif text-4xl mt-1 mb-2">Privacy Policy</h1>
                <p className="text-xs text-[var(--text-ink)]/55 mb-8">Last updated: February 2026</p>

                <Section title="Who we are">
                    Virtually Unstoppable is a productivity SaaS for students and professionals. We act as the data
                    controller for the personal information described below. Contact us at
                    <a href="mailto:hello@virtuallyunstoppable.app" className="text-[var(--brand-purple)]"> hello@virtuallyunstoppable.app</a>.
                </Section>

                <Section title="What data we collect">
                    <ul>
                        <li><strong>Account data</strong> — your name, email address, password hash (bcrypt), role
                            (student / professional), profile picture if you sign in with Google, and account timestamps.</li>
                        <li><strong>Content you create</strong> — clients, classes, projects, tasks, calendar events,
                            notes, habits, goals, finance entries, recipes, contracts, lectures, missed-lecture materials,
                            meeting notes, client memory entries, document uploads.</li>
                        <li><strong>Audio uploads &amp; lecture/meeting recordings</strong> — only used for transcription
                            and AI features you trigger. We do not retain raw audio beyond the time needed to transcribe it
                            unless you save the lecture/meeting record.</li>
                        <li><strong>Billing data</strong> — subscription status, trial expiry, Stripe customer ID. Card
                            details are handled exclusively by Stripe and never touch our servers.</li>
                        <li><strong>Operational logs</strong> — minimal request logs, error traces, and the day-streak ping.</li>
                    </ul>
                </Section>

                <Section title="How we use it">
                    <ul>
                        <li>To provide the service you signed up for (your dashboard, AI tools, billing).</li>
                        <li>To send transactional emails (password reset, email verification, billing receipts).</li>
                        <li>To protect the service against abuse and to comply with applicable law.</li>
                    </ul>
                    <p>We do <strong>not</strong> sell your data and we do <strong>not</strong> use your content to train
                        third-party AI models. AI prompts are sent to the LLM provider strictly to fulfil your request.</p>
                </Section>

                <Section title="Third-party processors">
                    <ul>
                        <li><strong>OpenAI</strong> (via the Emergent integration) — powers GPT-5.2 text generation and
                            Whisper-1 transcription. Prompts and audio are sent only when you trigger an AI feature.</li>
                        <li><strong>Stripe</strong> — payments and the Customer Portal.</li>
                        <li><strong>Resend</strong> — transactional email delivery.</li>
                        <li><strong>Google</strong> — only if you choose to sign in with Google.</li>
                        <li><strong>Emergent Object Storage</strong> — file uploads (documents, images).</li>
                        <li><strong>MongoDB Atlas</strong> — our managed database.</li>
                    </ul>
                </Section>

                <Section title="Where we store data">
                    Servers are located in the EU/UK and US (multi-region). Backups are retained for up to 30 days.
                </Section>

                <Section title="How long we keep it">
                    For as long as your account is active. When you delete your account, we delete your content within
                    30 days, except where retention is required by law (e.g. tax/billing records up to 7 years).
                </Section>

                <Section title="Your rights (UK GDPR / EU GDPR)">
                    You can access, correct, export, restrict, or delete your data at any time. Email
                    <a href="mailto:privacy@virtuallyunstoppable.app" className="text-[var(--brand-purple)]"> privacy@virtuallyunstoppable.app</a>
                    and we&apos;ll respond within 30 days. You also have the right to lodge a complaint with the UK ICO
                    or your local data protection authority.
                </Section>

                <Section title="Cookies">
                    We use a single first-party session cookie (<code>vu_token</code>) to keep you logged in. We do not
                    use third-party advertising cookies.
                </Section>

                <Section title="Manage your cookies">
                    You can revisit your choices any time —
                    <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent("vu:open-cookie-consent"))}
                        data-testid="privacy-manage-cookies"
                        className="ml-1 underline text-[var(--brand-purple)] hover:text-[var(--brand-magenta)]"
                    >
                        open the cookie preferences
                    </button>.
                </Section>

                <Section title="Children">
                    The service is not intended for children under 16.
                </Section>

                <Section title="Updates">
                    We&apos;ll post material changes on this page and notify active subscribers by email.
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-6">
            <h2 className="font-serif text-2xl mb-2">{title}</h2>
            <div className="text-sm text-[var(--text-ink)]/80 space-y-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1">
                {typeof children === "string" ? <p>{children}</p> : children}
            </div>
        </div>
    );
}
